import {
  isPermanentAutomaticReminderStopReason,
  reminderSendDate,
} from "@smart/bridge-types-basic";
import { convertUTCStringToZonedString } from "@smart/itops-utils-basic";

import { ReminderScheduleValue } from "../../types";

export type ReminderScheduleLimits =
  | {
      sendFollowUps?: boolean | null;
      maxFollowUps?: number | null;
    }
  | null
  | undefined;

export type ReminderEligibilityInput = {
  status?: string | null;
  sharedAt?: string | null;
};

type ReminderCounts =
  | {
      automaticCount?: number | null;
      manualStopTriggered?: boolean | null;
      lastAutomaticSentAt?: string | null;
      automaticStopReason?: string | null;
    }
  | null
  | undefined;

export const effectiveMaxReminders = (schedule: ReminderScheduleLimits) =>
  schedule?.sendFollowUps ? (schedule.maxFollowUps ?? 0) + 1 : 1;

export type ReminderNotice =
  | { kind: "already-stopped" }
  | { kind: "not-scheduled" }
  | { kind: "due-soon"; when: "today" | "next-weekday"; nextDate: string }
  | { kind: "will-stop"; remaining: number }
  | { kind: "schedule-unaffected"; nextDate: string };

const zonedDate = (now: string, timezone: string, daysToAdd = 0) =>
  convertUTCStringToZonedString(now, timezone, { daysToAdd }).slice(0, 10);

export const scheduleStillApplies = (
  submission: ReminderEligibilityInput,
  schedule: ReminderScheduleValue | null | undefined,
) => {
  if (!schedule?.enabled || !schedule.timezone) return false;
  if (submission.status !== "active") return false;
  if (!submission.sharedAt) return false;
  if (
    schedule.effectiveFrom &&
    zonedDate(submission.sharedAt, schedule.timezone) < schedule.effectiveFrom
  )
    return false;

  return true;
};

const nextWeekday = (date: string) => {
  const next = new Date(`${date}T00:00:00Z`);
  do {
    next.setUTCDate(next.getUTCDate() + 1);
  } while (next.getUTCDay() === 0 || next.getUTCDay() === 6);
  return next.toISOString().slice(0, 10);
};

export const buildReminderNotice = ({
  reminder,
  schedule,
  nextAutomaticReminderDate,
  now,
}: {
  reminder: ReminderCounts;
  schedule: ReminderScheduleValue | null | undefined;
  nextAutomaticReminderDate?: string | null;
  now: string;
}): ReminderNotice => {
  if (
    reminder?.manualStopTriggered ||
    isPermanentAutomaticReminderStopReason(reminder?.automaticStopReason)
  )
    return { kind: "already-stopped" };

  const automaticCount = reminder?.automaticCount ?? 0;
  if (
    reminder?.lastAutomaticSentAt &&
    automaticCount >= effectiveMaxReminders(schedule)
  )
    return { kind: "already-stopped" };

  if (!schedule?.enabled || !nextAutomaticReminderDate)
    return { kind: "not-scheduled" };

  if (schedule.stopOnManualSend) {
    return {
      kind: "will-stop",
      remaining: effectiveMaxReminders(schedule) - automaticCount,
    };
  }

  const nextDate = schedule.timezone
    ? reminderSendDate(nextAutomaticReminderDate, schedule.timezone, now)
    : nextAutomaticReminderDate;

  if (schedule.timezone) {
    const today = zonedDate(now, schedule.timezone);
    if (nextDate === today)
      return { kind: "due-soon", when: "today", nextDate };

    if (nextDate <= nextWeekday(today))
      return { kind: "due-soon", when: "next-weekday", nextDate };
  }

  return { kind: "schedule-unaffected", nextDate };
};

export const reminderActionLabel = (
  isInternalUse: boolean,
  showManageReminders: boolean,
  defaultLabel: string,
  manageRemindersLabel = "Manage Reminders",
) => {
  if (isInternalUse) return "Send";
  return showManageReminders ? manageRemindersLabel : defaultLabel;
};
